// The environment drawn by three, INSIDE the app's own scene.
//
// Which component mounts for a character follows the CANVAS (`Scene.jsx`,
// `environmentOwner.js`): a GLB character's environment has to composite with a
// GLB avatar, a ground plane and a shadow catcher, so it must be in the same
// scene and drawn by the same renderer. A splat character's environment goes
// into the ONE `GaussianSplat` the avatar already occupies, so the two depth-sort
// against each other — that is `ThreeSplatBridge`, not this.
//
// THREE THINGS HERE ARE LOAD-BEARING, and each was wrong once.
//
//   - THE SPHERICAL HARMONICS MUST BE ROTATED WITH THE POSE. The bake rewrites
//     position and covariance and leaves the coefficients alone, but SH are
//     DIRECTIONAL: three evaluates them against `center - localCameraPosition` in
//     the mesh's local space, so a rotation baked into the geometry and not into
//     the coefficients evaluates them against the wrong axes. Odd bands change
//     sign under a 180-degree flip and even bands do not, which is exactly the
//     pattern this produced — DC colour right, bands 1 and 3 each costing ~0.43
//     of wall mean against band 2's 0.16, and a green/red fringe on edges that
//     should be neutral. `shRotationForBake` is the fix; the SPZ reference
//     rotates SH on a frame change for the same reason (`rotFlipShFuncs` /
//     `flipSh` in load-spz.cc).
//
//     TWO "FIXES" MEASURED BETTER AND WERE BOTH WRONG — flipping the view
//     direction, and re-interleaving the bytes. Cross-checked against
//     PlayCanvas's gsplat shader, three's SH basis and its camera->splat
//     direction are correct to the last coefficient, and the SPZ reference stores
//     coefficient-major exactly as three reads it. Both were shrinking a signal
//     that was in the wrong FRAME, so neither survived.
//
//   - THE TONE CURVE APPLIES HERE AND DID NOT BEFORE. `GaussianSplat` is a
//     NodeMaterial and goes through the node output stack, so the room is
//     tone-mapped; the hand-written GLSL this replaced simply omitted three's
//     `<tonemapping_fragment>` chunk. ACES multiplies darks by ~0.2 and
//     compresses highlights, which is most of the level difference anyone will
//     notice. `material.toneMapped = false` does NOT reach it — only the
//     renderer-wide setting does, the same way it does not reach the probe
//     oracle.
//
//   - THE KERNEL IS CUT AT three's OWN 2 SIGMA, not the sqrt(8) ~ 2.83 the
//     previous renderer used. Drawn area goes as the cutoff squared and this pass
//     is fill-bound, so the wider cut costs about twice the fill for about 0.2 of
//     a level. It is also an aliasing difference: three cuts each Gaussian where
//     its alpha is still 13.5% of peak, against 1.8%.
//
// The A/B that established all of the above — including the things it RULED OUT
// (the decoders, the sort, the kernel cutoff on its own, the alpha attenuation,
// f32 precision, resolution) — is recorded in `docs/OGS_ARCHITECTURE.md`. Do not
// re-derive it from the levels on screen: the tone curve's gap dominates every
// per-splat term and inverted the verdict on two of them.
//
// ONE THING IS A GAIN RATHER THAN A MATCH: spherical harmonics. The previous
// route read decoded Gaussians back out splat by splat and got only the DC
// colour, so this environment has never had view-dependent colour. three's
// loaders emit `sphericalHarmonics1..3` already in three's own layout and they
// pass straight through, so the image is EXPECTED to differ.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { loadEnvironmentGeometry, splatFormat } from '../ogs/render/three/loadEnvironmentSplats.js'
import { estimateGeometryFloorY } from '../lib/splatFloorHeight.js'
import { bakeScale, shRotationForBake } from '../lib/shFrameRotation.js'
import { setEnvironmentBroken } from '../ogs/render/environmentOwner.js'
import { compilesNodeMaterials } from '../lib/nodeRendererState.js'
import { cameraSeeThrough } from '../lib/cameraSeeThrough.js'
import { partMounts } from '../lib/environmentParts.js'
import { configureSplatClass, SplatOrder, adoptStableOrder } from '../ogs/render/three/splatDraw.js'
import { ThreeStableSort } from '../ogs/render/three/threeStableSort.js'
import { placeParts } from '../lib/environmentPartPlacement.js'

/**
 * ONE splat of the environment: download it, bake its world placement into its
 * own attributes, and add it to the scene.
 *
 * `position`/`rotation`/`scale` are the part's WORLD placement — the environment's
 * pose already composed with the part's own local one by the wrapper below. The
 * composition happens there rather than here because a part carries a LOCAL
 * transform (`environmentParts.js`: world = envPose ∘ partPose) and this baker
 * knows only about one matrix.
 *
 * @param {object} p
 * @param {string} p.url            the row's `file_url` or the part's `url`, verbatim
 * @param {string} [p.format]       the row's declared format; the extension otherwise
 * @param {number|string} [p.version] changes → re-fetch (the row's updatedAt)
 * @param {number[]} [p.position]
 * @param {number[]} [p.rotation]
 * @param {number} [p.scale]
 * @param {(y: number) => void} [p.onFloorY]  only the floor-source part gets one
 * @param {(e: Error) => void} [p.onError]
 * @param {(patch: object) => void} [p.onState] load progress and the live mesh
 */
function EnvironmentSplatPart({
  url, format, version, position = [0, 0, 0], rotation = [0, 0, 0], scale = 1,
  onFloorY, onError, onState,
}) {
  const scene = useThree((s) => s.scene)
  const gl = useThree((s) => s.gl)
  const camera = useThree((s) => s.camera)
  const splatRef = useRef(null)
  const sortRef = useRef(null)
  // Callbacks in a ref, never in the dep array: `Scene` re-renders constantly and
  // an inline `onFloorY` would remount this on every one of them — which here
  // means re-parsing 43 MB. That exact mistake mounted a second renderer over
  // 2.4M splats in `ThreeEnvSplat`.
  const cbRef = useRef({ onFloorY, onError, onState })
  cbRef.current = { onFloorY, onError, onState }

  const px = position[0], py = position[1], pz = position[2]
  const rx = rotation[0], ry = rotation[1], rz = rotation[2]

  useEffect(() => {
    if (!url) return undefined
    if (!splatFormat(url, format)) {
      // LOUD. A format three has no loader for is a data problem, and the
      // alternative — mounting nothing — renders an empty room that reads as a
      // working renderer.
      const e = new Error(`[three-env] no loader for "${url}" (format ${format ?? 'unknown'})`)
      console.error(e.message)
      cbRef.current.onError?.(e)
      return undefined
    }

    let cancelled = false
    let splat = null
    const state = (patch) => { if (!cancelled) cbRef.current.onState?.(patch) }
    state({ started: true, received: 0, total: 0, downloadFrac: 0, decodeFrac: 0, loaded: false, error: null })

    ;(async () => {
      try {
        // Baked into the splats rather than set on the mesh, so this path and the
        // bridge's share one space and cannot drift.
        const matrix = new THREE.Matrix4().compose(
          new THREE.Vector3(px, py, pz),
          new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)),
          new THREE.Vector3(scale, scale, scale),
        )
        const t0 = performance.now()
        // THE BAKE MOVES THE GEOMETRY AND NOT THE COEFFICIENTS, which is the defect
        // `setShRotation` (below) exists to close. SH are DIRECTIONAL — three evaluates
        // them against `center - localCameraPosition` in the mesh's local space — so a
        // rotation baked into position and covariance but not into the coefficients
        // evaluates the view-dependent term against the wrong axes. Startup_Level's
        // pose is a 180 degree flip, the worst case: odd bands change sign under it and
        // even ones do not, which predicted the whole measured pattern (DC colour
        // right, bands 1 and 3 costing ~0.43 of wall mean each against band 2's 0.16).
        // Two "fixes" that appeared to help were provably wrong — flipping the view
        // direction is a second 180 that partly cancels the first, and re-interleaving
        // the coefficient bytes only shrinks a signal that is in the wrong frame.
        // The pose is BAKED into the geometry, and `setShRotation` (below) tells the
        // evaluator how to get back to the coefficients' own frame. That is the form
        // the bridge can also use, which shares ONE mesh with the avatar and has no
        // choice but to bake.
        const geo = await loadEnvironmentGeometry({
          url, format, matrix,
          // THE ROW'S `updatedAt`, and it is what makes the app's splat cache
          // usable here: the key is (url, version), so a warm reload is instant
          // and a RE-UPLOAD — which lands under the same url — misses and
          // re-downloads. Without it a re-uploaded room serves its old bytes.
          version,
          // Bytes off the wire, read off the streamed response. `total` is 0
          // whenever the server sends no `Content-Length`, which reports as
          // UNKNOWN (-1) rather than as a bar stuck at zero.
          onProgress: (e) => state({
            received: e?.loaded ?? 0,
            total: e?.total ?? 0,
            downloadFrac: e?.total ? Math.min(1, e.loaded / e.total) : -1,
          }),
        })
        // The parse has no progress to report: three's loaders decode in one
        // call. -1 is UNKNOWN, which the overlay draws as indeterminate — the
        // honest answer, where 0 would read as a stall.
        state({ decodeFrac: -1 })
        if (cancelled) { geo.dispose?.(); return }

        // THE FLOOR IS REPORTED WHETHER OR NOT THE ROOM IS DRAWN, and that is
        // the whole reason the parse still runs on a renderer that cannot draw
        // it. `__ENV_FLOOR_Y__` places the avatar, the shadow sun and the ground
        // plane; without it the character stands in a hole, which would make the
        // classic renderer useless as the A/B it exists to be.
        //
        // The floor UNDER THE CHARACTER, not the lowest splat in the room. The
        // bounding box's min.y was used here briefly and is wrong by 32 cm on
        // Startup_Level — see splatFloorHeight.js. Nothing errors when it is
        // wrong; the avatar just stands in a hole.
        const floorY = estimateGeometryFloorY(geo)
        if (cbRef.current.onFloorY) {
          if (Number.isFinite(floorY)) cbRef.current.onFloorY(floorY)
          else console.warn('[three-env] not enough splats under the character to estimate the floor')
        }

        // THE RENDERER IS ASKED, ONCE, BEFORE THE MESH IS BUILT.
        //
        // ASKED OF THE RENDERER, AND A REFUSAL IS LOUD. `GaussianSplat` is a
        // NodeMaterial and it also runs a COMPUTE pass for its spherical
        // harmonics; a renderer with neither throws `renderer.compute is not a
        // function` INSIDE the render loop, and an exception there does not cost
        // you the room, it costs you the FRAME — measured, the viewport drew
        // nothing at all: no avatar, no ground, no grid. One object that cannot
        // be drawn took down every object that could.
        //
        // The app's own canvas is always a node renderer, so this is unreachable
        // there. It is kept because this component is also mounted by harnesses
        // that build their own canvas (`?debug_env=1` did exactly that and
        // reported `not-drawn`), and because the honest degradation — no room,
        // and a line saying why — is the one thing that cannot be arrived at by
        // accident.
        if (!compilesNodeMaterials(gl)) {
          const why = `[three-env] ${gl?.constructor?.name ?? 'this renderer'} compiles no node material, `
            + 'so the environment splat is NOT DRAWN. Lighting is unaffected (the probe grid and the '
            + 'reflection captures are baked textures) and the floor is still estimated from the '
            + 'parsed splats. Mount this under the canvas `canvasGlFactory()` builds.'
          console.warn(why)
          // LOADED, not drawn. The bytes arrived and the floor came out of them,
          // so the overlay must stop rather than spin forever over a room that is
          // never coming.
          state({
            decodeFrac: 1, loaded: true,
            drawn: 'not-drawn', reason: why, renderer: gl?.constructor?.name ?? null, floorY,
          })
          geo.dispose?.()
          return
        }

        const { GaussianSplat } = await import('../ogs/render/three/threeSplatDeps.js')
        // `seeThrough: true` costs nothing until driven — `strength` defaults to 0
        // and `mix( 1, f, 0 )` is exactly 1 — so this is always on rather than
        // gated on the current config, which would need a rebuild (and a re-parse
        // of tens of MB) the moment someone enabled it from the panel.
        // THE ROOM'S RASTER, SETTLED. Both values are CLASS statics shared with the
        // OGS bridge, which re-asserts its own on every build — so each path writes
        // what it wants unconditionally rather than reading what the other left.
        //
        // THE BLUR'S ALPHA ATTENUATION IS ON, FLOORED. Unbounded it does two jobs and
        // only one is wanted: `sqrt(detBase/det)` conserves the +KERNEL_2D_SIZE blur's
        // integral on large overlapping splats, which composites the tile faces more
        // evenly, and it goes to ZERO for a sub-pixel splat, which erases the tile
        // grout. The floor keeps the first and bounds the second. The pixel metric
        // against the pre-three build prefers it OFF (wall/floor mean |RGB| 1.65/4.47
        // off, 2.35/5.47 here); it is on anyway because that metric measures agreement
        // with a reference that stopped being the target once the room joined the
        // scene's tone curve, not whether the image is good. The AVATAR path takes the
        // opposite default — its decoders were fit against a rasterizer that does not
        // attenuate, where this erases teeth and eyeballs outright.
        //
        // THE KERNEL IS CUT AT three's OWN 2 SIGMA. sparkjs cuts at `maxStdDev` =
        // sqrt(8) = 2.83 and that is measurably better — three truncates each Gaussian
        // where its alpha is still exp(-2) = 13.5% of peak against sparkjs's 1.8%, so 2
        // leaves a step on every splat, worth ~0.2 of one level in 255. It costs double:
        // drawn area goes as the cutoff SQUARED, on every one of 2.4M splats, in a pass
        // whose frame time is linear in pixels with a fixed cost of a few ms — i.e.
        // almost entirely overdraw.
        // The attenuation is a PER-INSTANCE option (below); the cutoff is a
        // class-wide value, and correctly so — both paths want 2 and the reason is
        // the same on both. It is set together with the RADIAL SORT METRIC, by the
        // one module that owns both (`splatDraw.js`): the metric used to be set by
        // the avatar's bridge alone, and since it is a class static, a room mounted
        // for a GLB character — with no bridge in the page — sorted on three's
        // planar `-viewZ` and washed its thin dark features out.
        configureSplatClass(GaussianSplat)

        // THE HARMONICS' FRAME, DECIDED BEFORE THE MESH IS BUILT. The constructor
        // copies them into storage buffers, so a failure handled afterwards would
        // drop an attribute nothing reads any more and draw them anyway.
        //
        // The bake moved the geometry and not the coefficients, so the evaluator is
        // told how to get back. The rule and its refusals live in
        // `shFrameRotation.js`, unit-tested as a ROUND TRIP rather than as a matrix
        // — a transpose the wrong way round still looks plausible.
        let shRotation = null
        {
          try {
            shRotation = shRotationForBake(matrix)
          } catch (e) {
            // A non-uniform pose has no such rotation. LOUD, and the harmonics are
            // dropped rather than drawn in the wrong frame — a fringe that reads as
            // a renderer bug is worse than no view-dependent colour.
            console.error('[three-env] cannot place the spherical harmonics:', e.message)
            for (let b = 1; b <= 3; b++) geo.deleteAttribute(`sphericalHarmonics${b}`)
          }
        }

        splat = new GaussianSplat(geo, {
          // three's own sort, HANDED OVER on the first frame ours submits (see the
          // `useFrame` below). It cannot start at false: if the stable sort never
          // manages to run, a mesh with no order at all does not degrade, it
          // renders as noise.
          autoSort: true,
          seeThrough: true,
          // THE ROOM ATTENUATES, THE AVATAR DOES NOT, and both meshes can be alive
          // in one frame — a splat character whose bridge hands the environment
          // back late draws its avatar from `ThreeSplatBridge` while this component
          // draws the room. So this rides the constructor rather than a class
          // static, where whichever mesh was built last would decide for both.
          alphaAttenuation: true,
          alphaAttenuationFloor: 0.4,
        })
        // THE STABLE ORDER. This component carried the code to DRIVE one — the
        // per-frame reads below, the buffer lookups, the comment about the
        // ordering guarantee — and nothing ever built it, so every read fell
        // through and the room kept three's binned sort, whose order within a bin
        // is thread-arrival order (96.8% of slots disagree between two runs over
        // identical splats). That is the flicker the radix exists to remove, and
        // it was shipping on this path the whole time.
        {
          const device = gl?.backend?.device ?? null
          sortRef.current = device ? { splat, order: new SplatOrder(new ThreeStableSort(device)) } : null
          if (!device) console.warn('[three-env] no WebGPU device for the stable sort — '
            + "the room keeps three's binned order, which re-randomises overlapping splats")
        }
        if (shRotation && splat.setShRotation) {
          splat.setShRotation(shRotation)
          console.info('[three-env] spherical harmonics rotated back into the file frame '
            + `(pose scale ${bakeScale(matrix).toFixed(4)})`)
        }


        // The room always surrounds the camera; culling it by its own bounds only
        // ever costs a frame where it vanishes at the edge of a turn.
        splat.frustumCulled = false
        scene.add(splat)
        splatRef.current = splat

        const n = geo.getAttribute('position').count
        const bands = [1, 2, 3].filter((b) => geo.getAttribute(`sphericalHarmonics${b}`)).length
        console.info(`[three-env] ${n.toLocaleString()} splats in `
          + `${Math.round(performance.now() - t0)} ms, SH degree ${bands || 0}`)
        state({
          decodeFrac: 1, loaded: true, mesh: splat,
          drawn: 'drawn', count: n, shDegree: bands || 0,
          renderer: gl?.constructor?.name ?? null, floorY,
        })
      } catch (e) {
        if (cancelled) return
        console.error('[three-env] failed to load the environment:', e)
        // The same verdict the bridge publishes, and for the same reason: a file
        // that cannot be decoded cannot be decoded by the other path either, and
        // the scene must stop presenting as though it had an environment or the
        // viewport is BLACK — the studio stage and the backdrop are gated on
        // there being none.
        setEnvironmentBroken(String(e?.message ?? e))
        state({
          error: String(e?.message ?? e), decodeFrac: 1,
          drawn: 'error', reason: String(e?.message ?? e),
        })
        cbRef.current.onError?.(e)
      }
    })()

    return () => {
      cancelled = true
      onState?.({ mesh: null })
      if (splat) {
        if (splatRef.current === splat) splatRef.current = null
        scene.remove(splat)
        // The mesh's own teardown (PATCHED (aos) in `GaussianSplatPatched.js`):
        // geometry, material and the source splat geometry, which these two
        // lines used to leave alive.
        splat.dispose?.()
        splat = null
      }
      if (sortRef.current) {
        sortRef.current.order.dispose()
        sortRef.current = null
      }
    }
  }, [url, format, version, px, py, pz, rx, ry, rz, scale, scene, gl]) // eslint-disable-line react-hooks/exhaustive-deps

  // Per frame, and deliberately UNCONDITIONAL: the uniforms are three floats and
  // a pair of vec3s, so writing them when the effect is off costs less than the
  // branch that would ask. `strength` 0 is an exact no-op in the shader, so "off"
  // and "not wired" are the same render — which is the point, since the previous
  // behaviour was "not wired" pretending to be "off".
  useFrame(() => {
    const splat = splatRef.current
    if (!splat) return
    // The order is submitted on the same queue, ahead of the raster that reads
    // it — submission order IS the ordering guarantee.
    const ss = sortRef.current
    if (ss) {
      // The buffer lookups live in `splatDraw.js` — they are the part that fails
      // SILENTLY, and they failed here for both meshes' worth of reasons.
      adoptStableOrder(ss.splat, ss.order.run(gl, ss.splat, camera.position))
    }
    // The stock addon has no see-through uniforms to drive.
    if (splat.userData?.aosStock) return
    const p = cameraSeeThrough.readParams()
    // `setSeeThrough` takes points in the SPLATS' OWN space. With the pose baked
    // in, that is world space and these go through unchanged; with the pose on the
    // mesh it is not, and they have to be inverse-transformed — otherwise the fade
    // tracks a camera/head pair that is 180 degrees away from where they are and
    // the effect silently points at nothing.
    _a.set(p.camPos[0], p.camPos[1], p.camPos[2])
    _b.set(p.headPos[0], p.headPos[1], p.headPos[2])
    if (splat.userData?.aosPosedMesh === true) {
      _inv.copy(splat.matrixWorld).invert()
      _a.applyMatrix4(_inv)
      _b.applyMatrix4(_inv)
    }
    splat.setSeeThrough(_a, _b, p.radius, p.fade, p.active ? p.strength : 0)
  })

  return null
}

/**
 * THE ENVIRONMENT, which is one splat or several.
 *
 * An environment can be split into PARTS (ENG-1132) — the furniture as its own
 * file, separate from the walls — so the room can be authored, tagged and walked
 * to piece by piece. `partMounts` turns the row's part map into a list, and an
 * environment with no parts comes back as a one-entry list holding its own file,
 * so there is one code path either way rather than a special case for the common
 * shape.
 *
 * EACH PART BAKES ITS OWN WORLD MATRIX, and that is the difference from the
 * component this replaced. That one put every part inside ONE posed group, which
 * is what a mesh carrying its own transform needs. Here the pose is baked into
 * the attributes — the same space the avatar's own bridge uses, so the two cannot
 * drift — and a baked mesh has no transform left to inherit. So the composition
 * `world = envPose ∘ partPose` happens HERE, once, and each part is handed the
 * result.
 *
 * ONE ROOM, ONE SET OF NUMBERS. The `__SPLAT_*` globals the loading overlay and
 * the bake tool read describe the ENVIRONMENT, so they are aggregated here and
 * published once: bytes summed so a single bar only moves forwards, and
 * `__SPLAT_LOADED__` true only when EVERY part has landed, because a room that is
 * half there is not a room the avatar can be placed in. A part writing them
 * itself would mean the last one to report wins.
 *
 * @param {object} p
 * @param {string} p.url              the row's `file_url`, verbatim
 * @param {string} [p.format]         the row's declared format; the extension otherwise
 * @param {object} [p.parts]          the row's part map, as `readParts` returns it
 * @param {number|string} [p.version] cache-busting token; changes → re-fetch
 * @param {number[]} [p.position]     the environment's own pose
 * @param {number[]} [p.rotation]
 * @param {number} [p.scale]
 * @param {(y: number) => void} [p.onFloorY]
 * @param {(e: Error) => void} [p.onError]
 */
export default function ThreeEnvironmentSplat({
  url, format, parts = null, version, position = [0, 0, 0], rotation = [0, 0, 0],
  scale = 1, onFloorY, onError,
}) {
  const px = position[0], py = position[1], pz = position[2]
  const rx = rotation[0], ry = rotation[1], rz = rotation[2]

  const { mounts, multipart } = useMemo(() => partMounts(parts, url), [parts, url])

  // World placement per part, composed once by `environmentPartPlacement.js` —
  // a leaf module so the composition is unit-testable against what the scene
  // graph used to produce, which is the one thing this port could get subtly
  // wrong. Memoised on the FLATTENED numbers rather than on the arrays: `Scene`
  // rebuilds `position`/`rotation` on every render, so an array identity here
  // would recompose — and therefore remount and re-download — the whole room
  // constantly.
  const placed = useMemo(
    () => placeParts(mounts, { position: [px, py, pz], rotation: [rx, ry, rz], scale }),
    [mounts, px, py, pz, rx, ry, rz, scale],
  )

  // Per-part load state, keyed by part name. A ref, not state: nothing renders
  // from it, and re-rendering the whole room every time one of several
  // hundred-megabyte parts reports a progress event is pure cost. An ERROR is the
  // exception — it has to reach the render pass, so it bumps a counter.
  const statesRef = useRef(new Map())
  const meshesRef = useRef(new Map())
  const [, bumpErr] = useState(0)

  const publish = useCallback(() => {
    if (typeof window === 'undefined') return
    const states = [...statesRef.current.values()]
    const received = states.reduce((n, s) => n + (s.received ?? 0), 0)
    const total = states.reduce((n, s) => n + (s.total ?? 0), 0)
    const anyUnknown = states.some((s) => s.downloadFrac === -1)
    const decodes = states.map((s) => s.decodeFrac ?? 0)

    window.__SPLAT_LOAD_STARTED__ = states.some((s) => s.started)
    window.__SPLAT_BYTES_RECEIVED__ = received
    window.__SPLAT_BYTES_TOTAL__ = total
    window.__SPLAT_LOAD_PROGRESS__ = anyUnknown ? -1 : (total > 0 ? Math.min(1, received / total) : 0)
    // The MEAN across parts, so four decoding together show one bar rather than
    // four restarting from zero. A -1 from any part makes the whole thing
    // unknown: averaging it in would read as progress going backwards.
    window.__SPLAT_DECODE_PROGRESS__ = decodes.some((d) => d === -1)
      ? -1
      : (decodes.length ? decodes.reduce((a, b) => a + b, 0) / decodes.length : 0)
    window.__SPLAT_LOADED__ = states.length > 0 && states.every((s) => s.loaded)
    window.__SPLAT_LOAD_ERROR__ = states.map((s) => s.error).find(Boolean) ?? null
    // The floor-source mesh stays the single `__SPLAT_MESH__` every existing
    // reader already asks for (the bake tool, specs, the console): for a one-file
    // environment it is the same object as before, and for a multipart one it is
    // the room scan rather than whichever part finished last.
    const floorName = placed.find((m) => m.isFloorSource)?.name
    window.__SPLAT_MESH__ = (floorName && meshesRef.current.get(floorName)) ?? null
    window.__SPLAT_MESHES__ = Object.fromEntries(meshesRef.current)
    window.__SPLAT_POSE__ = placed.length ? {
      position: [px, py, pz], rotation: [rx, ry, rz], scale,
      parts: placed.map((m) => m.name),
      multipart,
    } : null

    // THE ROOM'S VERDICT, AGGREGATED — `drawn`, `not-drawn` (the renderer
    // compiles no node material) or `error`. It is published HERE, once, for the
    // same reason the numbers above are: those three states look identical from
    // outside, and with several parts in flight a part writing this itself means
    // the last one to report wins. One part's teardown would then delete the
    // global while the rest are still drawn, and an error on the sofa would erase
    // the walls' `drawn`.
    //
    // NO `state` KEY UNTIL EVERY PART HAS A VERDICT. Readers poll for `.state`,
    // so answering early is answering about part of a room — and the answer they
    // would get is the optimistic one.
    const verdicts = states.map((s) => s.drawn).filter(Boolean)
    const floorSource = statesRef.current.get(floorName)
    // The WORST verdict wins: a room with one broken part is a broken room, and
    // reporting the majority answer would hide it.
    const worst = verdicts.includes('error') ? 'error'
      : verdicts.includes('not-drawn') ? 'not-drawn'
        : 'drawn'
    const firstReason = states.map((s) => s.reason).find(Boolean) ?? null
    if (placed.length === 0) {
      delete window.__THREE_ENV_SPLAT__
    } else {
      window.__THREE_ENV_SPLAT__ = {
        // `count` SUMS the drawn parts: it is the room's splat count, not
        // whichever part happened to finish last.
        count: states.reduce((n, s) => n + (s.count ?? 0), 0),
        parts: placed.length,
        multipart,
        // The FLOOR comes from the floor-source part alone — the room scan, not
        // the sofa, whose lowest splat is not where the character stands.
        floorY: floorSource?.floorY,
        renderer: states.find((s) => s.renderer)?.renderer ?? null,
        shDegree: states.find((s) => s.shDegree != null)?.shDegree,
        ...(verdicts.length === placed.length
          ? { state: worst, ...(worst === 'drawn' ? {} : { reason: firstReason }) }
          : { pending: placed.length - verdicts.length }),
      }
    }
  }, [placed, multipart, px, py, pz, rx, ry, rz, scale])

  const handleState = useCallback((name, patch) => {
    if ('mesh' in patch) {
      if (patch.mesh) meshesRef.current.set(name, patch.mesh)
      else meshesRef.current.delete(name)
    }
    const prev = statesRef.current.get(name) ?? {}
    statesRef.current.set(name, { ...prev, ...patch })
    publish()
    if (patch.error) bumpErr((n) => n + 1)
  }, [publish])

  // Forget the parts that are gone, so a removed one cannot hold
  // `__SPLAT_LOADED__` at false for an environment that is fully loaded.
  useEffect(() => {
    const live = new Set(placed.map((m) => m.name))
    for (const name of [...statesRef.current.keys()]) {
      if (!live.has(name)) { statesRef.current.delete(name); meshesRef.current.delete(name) }
    }
    publish()
  }, [placed, publish])

  useEffect(() => () => {
    if (typeof window === 'undefined') return
    window.__SPLAT_LOADED__ = false
    window.__SPLAT_LOAD_STARTED__ = false
    window.__SPLAT_MESH__ = null
    window.__SPLAT_MESHES__ = null
    window.__SPLAT_POSE__ = null
    delete window.__THREE_ENV_SPLAT__
  }, [])

  // THE CONFIG AND THE FEED ARE NOT HERE. They used to be, and that made the
  // fade a property of WHO DRAWS the environment: this component is not mounted
  // for a splat character (its avatar's own mesh owns the room, see
  // `environmentOwner`), so see-through was configured by nobody and inert on
  // exactly that path. `cameraSeeThrough.attach()` follows `liveTuneStore`
  // instead, once, for every owner. This component only READS `readParams()`
  // when it draws.

  return placed.map((m) => (
    <EnvironmentSplatPart
      // Keyed by URL, not by name: the base part is mounted under its URL BEFORE
      // the row's parts are known (a reload restores the url alone and Scene
      // resolves the parts a moment later), so keying by name would remount it
      // the instant they arrive and re-download the whole room scan.
      key={m.key}
      url={m.url}
      format={m.isFloorSource ? format : undefined}
      version={version}
      position={m.world.position}
      rotation={m.world.rotation}
      scale={m.world.scale}
      onFloorY={m.isFloorSource ? onFloorY : undefined}
      onError={onError}
      onState={(patch) => handleState(m.name, patch)}
    />
  ))
}

// Module-level scratch: `useFrame` runs every frame and the CLAUDE.md rule
// against per-frame allocation applies here as much as in the graph evaluator.
const _a = new THREE.Vector3()
const _b = new THREE.Vector3()
const _inv = new THREE.Matrix4()
